/**
 * Box 4 of the IAP cover: Approved by Incident Commander(s).
 *
 * A unified command approves the plan jointly, so the box takes one signature per Incident Commander rather than being
 * claimed by whoever reaches it first. Every IC on the roster gets a row whether or not they have signed, which is how
 * the paper form reads: the names are printed, and the approval is what gets added.
 *
 * Signing is deliberately here rather than on the version bar above. The shared bar offers the next *unfilled* slot,
 * and this box counts as filled the moment the first commander signs, so a second one would never be offered it.
 */

import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Check } from 'lucide-react';

import { selectUser } from 'slices/commonSelectors';
import {
  useAppendFormSignature,
  useFormVersionTip,
} from 'components/IAP/hooks/icsFormVersionHooks';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { isIncidentCommanderSeatName } from 'components/IncidentConfig/CreateIncident/draftRosterModel';
import {
  formatSignatureDateTime,
  parseWalaSignatures,
  type WalaSignature,
} from '../AssignmentList/ics204SignatureSlots';
import Ics204SignDialog from '../AssignmentList/Ics204SignDialog';

import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import { IAP_COVER_BASE_ID, IAP_COVER_IC_APPROVAL_ROLE } from './iapCoverForm';

type IapCoverApprovalTableProps = {
  periodNumber: number;
  organizationName: string;
  /** True once an IAP has been generated for this period; approval is closed until then. */
  iapGenerated: boolean;
  readOnly?: boolean;
};

type ApprovalRow = {
  key: string;
  userGuid?: string;
  name: string;
  organization: string;
  signature?: WalaSignature;
};

/**
 * One row per Incident Commander, plus a row for anyone who signed but has since come off the roster.
 *
 * Signatures are matched on the signer's id where there is one; the name is a fallback for approvals recorded before
 * the id was captured. A signature with no matching seat still prints, because the plan really was approved by them.
 */
export function buildApprovalRows({
  commanders,
  signatures,
  organizationName,
}: {
  commanders: { userId: string; name: string }[];
  signatures: WalaSignature[];
  organizationName: string;
}): ApprovalRow[] {
  const approvals = signatures.filter(
    (signature) => signature.role === IAP_COVER_IC_APPROVAL_ROLE
  );
  const claimed = new Set<WalaSignature>();

  const seated = commanders.map((commander) => {
    const signature = approvals.find(
      (candidate) =>
        !claimed.has(candidate) &&
        (candidate.userGuid
          ? candidate.userGuid === commander.userId
          : candidate.name === commander.name)
    );
    if (signature) claimed.add(signature);
    return {
      key: commander.userId,
      userGuid: commander.userId,
      name: commander.name,
      organization: organizationName,
      signature,
    };
  });

  const unseated = approvals
    .filter((signature) => !claimed.has(signature))
    .map((signature, index) => ({
      key: `unseated-${index}-${signature.signedAt}`,
      name: signature.name,
      organization: organizationName,
      signature,
    }));

  return [...seated, ...unseated];
}

const IapCoverApprovalTable: React.FC<IapCoverApprovalTableProps> = ({
  periodNumber,
  organizationName,
  iapGenerated,
  readOnly = false,
}) => {
  const user = useSelector(selectUser);
  const { data: tip } = useFormVersionTip(IAP_COVER_BASE_ID, periodNumber);
  const appendSignature = useAppendFormSignature(
    IAP_COVER_BASE_ID,
    periodNumber
  );
  const [signing, setSigning] = useState(false);

  const { data: commanders = [] } = useIncidentRoster((roster) =>
    roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          isIncidentCommanderSeatName(position.positionName)
        )
      )
      .map((member) => ({
        userId: member.userId,
        name: member.userName || member.emailAddress || '',
      }))
  );

  const signatures = useMemo(
    () => parseWalaSignatures(tip?.signatures),
    [tip?.signatures]
  );
  const rows = useMemo(
    () => buildApprovalRows({ commanders, signatures, organizationName }),
    [commanders, signatures, organizationName]
  );

  const currentUserGuid = user?.user_guid;
  const viewerIsCommander = commanders.some(
    (commander) => commander.userId === currentUserGuid
  );
  const viewerHasSigned = rows.some(
    (row) => row.userGuid === currentUserGuid && row.signature
  );
  // The preparer opens the signed version; until they have, there is nothing to
  // approve, which is the same rule every other form's approval box follows.
  const preparedBy = signatures[0] != null;

  const blockedReason = !iapGenerated
    ? 'Generate the IAP before approving it.'
    : !preparedBy
      ? 'The preparer must sign the cover before it can be approved.'
      : null;

  const canSign =
    !readOnly && viewerIsCommander && !viewerHasSigned && !blockedReason;

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <Table data-testid="iap-cover-approval-table">
        <TableHeader>
          <TableRow>
            <TableHead>Organization</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Signature</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={3} className="tw:text-muted-foreground">
                No Incident Commander is assigned on the roster.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow key={row.key} data-testid="iap-cover-approval-row">
                <TableCell>{row.organization}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>
                  {row.signature ? (
                    <span className="tw:flex tw:flex-col">
                      <span className="tw:italic">{row.signature.name}</span>
                      <span className="tw:text-xs tw:text-muted-foreground">
                        {formatSignatureDateTime(row.signature.signedAt)}
                      </span>
                    </span>
                  ) : (
                    <span className="tw:text-muted-foreground">
                      Not approved
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {!readOnly && viewerIsCommander && !viewerHasSigned && (
        <div className="tw:flex tw:items-center tw:gap-2">
          <Button
            disabled={!canSign || appendSignature.isPending}
            onClick={() => setSigning(true)}
            data-testid="iap-cover-approve"
          >
            <Check className="tw:size-4" />
            Approve as Incident Commander
          </Button>
          {blockedReason && (
            <span className="tw:text-xs tw:text-muted-foreground">
              {blockedReason}
            </span>
          )}
        </div>
      )}

      <Ics204SignDialog
        open={signing}
        role={IAP_COVER_IC_APPROVAL_ROLE}
        title="Approve the Incident Action Plan"
        description="Your approval is printed in box 4 alongside the other Incident Commanders."
        confirmLabel="Approve"
        defaultName={
          user?.profile_settings?.userName?.trim() || user?.user_name?.trim() || ''
        }
        isPending={appendSignature.isPending}
        onClose={() => setSigning(false)}
        onConfirm={(name) => {
          appendSignature.mutate({
            name,
            role: IAP_COVER_IC_APPROVAL_ROLE,
            signedAt: Date.now(),
          });
          setSigning(false);
        }}
      />
    </div>
  );
};

export default IapCoverApprovalTable;
